import type { NumRangeLike } from "@tento-core/range";
import { PlotCollection, type PlotRequest } from "@tento-data";

export type NumericExtent = Readonly<{
  min: number;
  max: number;
}>;

export function toCanvasX(
  x: number,
  xRange: NumRangeLike,
  width: number,
): number {
  const span = xRange.end - xRange.start;
  if (span === 0) return 0;
  return ((x - xRange.start) / span) * width;
}

export function toCanvasWidth(
  domainWidth: number,
  xRange: NumRangeLike,
  width: number,
): number {
  const span = xRange.end - xRange.start;
  if (span === 0) return 0;
  return (domainWidth / span) * width;
}

export function toCanvasY(
  y: number,
  extent: NumericExtent,
  height: number,
): number {
  const span = extent.max - extent.min;
  if (span === 0) return height / 2;
  const progress = (y - extent.min) / span;
  return height - progress * height;
}

export function includeZero(extent: NumericExtent): NumericExtent {
  return normalizeExtent({
    min: Math.min(0, extent.min),
    max: Math.max(0, extent.max),
  });
}

/**
 * The y-extent a renderer should map values against: the canvas-wide
 * shared extent from `request.yExtent` when one is imposed (so every
 * series shares one scale and a y-axis can label it), otherwise the
 * entry's own auto extent over `fallbackRange` (each renderer passes the
 * range it previously auto-scaled against — `viewRange` or `paddedRange`).
 */
export function effectiveYExtent(
  entry: PlotCollection.AnyEntry,
  request: PlotRequest,
  fallbackRange: NumRangeLike = request.viewRange,
): Option<NumericExtent> {
  if (request.yExtent) {
    return normalizeExtent({
      min: request.yExtent.min,
      max: request.yExtent.max,
    });
  }
  return resolveYExtent(entry, fallbackRange);
}

/**
 * The union of every entry's auto y-extent — the canvas-wide auto-fit scale
 * shared by all series (and labelled by the y-axis). `xRange` windows the
 * scan; pass `null` for a GLOBAL extent over every point (so the scale does
 * not re-fit as the x-axis pans). Returns `null` when no entry yields a
 * finite extent.
 */
export function unionYExtent(
  entries: readonly PlotCollection.AnyEntry[],
  xRange: Option<NumRangeLike>,
): Option<NumericExtent> {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const entry of entries) {
    const extent = resolveYExtent(entry, xRange);
    if (extent == null) continue;
    min = Math.min(min, extent.min);
    max = Math.max(max, extent.max);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return normalizeExtent({ min, max });
}

/**
 * Auto y-extent for one entry. `xRange` windows the point scan; `null` scans
 * every point (global, pan-stable).
 */
export function resolveYExtent(
  entry: PlotCollection.AnyEntry,
  xRange: Option<NumRangeLike>,
): Option<NumericExtent> {
  const hinted = entry.descriptor.yRange;
  if (hinted !== null && hinted !== undefined) {
    return normalizeExtent({
      min: hinted.min,
      max: hinted.max,
    });
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  if (PlotCollection.isScalarEntry(entry)) {
    const pts = xRange
      ? entry.series.range(xRange.start, xRange.end).points()
      : entry.series.points();
    for (const [_x, value] of pts) {
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
  } else if (PlotCollection.isBarWidthEntry(entry)) {
    const pts = xRange
      ? entry.series.range(xRange.start, xRange.end).points()
      : entry.series.points();
    for (const [_x, value] of pts) {
      min = Math.min(min, value.y);
      max = Math.max(max, value.y);
    }
  } else if (PlotCollection.isRangeYEntry(entry)) {
    const pts = xRange
      ? entry.series.range(xRange.start, xRange.end).points()
      : entry.series.points();
    for (const [_x, value] of pts) {
      min = Math.min(min, value.low);
      max = Math.max(max, value.high);
    }
  } else if (PlotCollection.isOhlcEntry(entry)) {
    const pts = xRange
      ? entry.series.range(xRange.start, xRange.end).points()
      : entry.series.points();
    for (const [_x, value] of pts) {
      min = Math.min(min, value.low);
      max = Math.max(max, value.high);
    }
  } else if (PlotCollection.isPointEntry(entry)) {
    const pts = xRange
      ? entry.series.range(xRange.start, xRange.end).points()
      : entry.series.points();
    for (const [_x, value] of pts) {
      min = Math.min(min, value.y);
      max = Math.max(max, value.y);
    }
  } else {
    return null;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return normalizeExtent({ min, max });
}

function normalizeExtent(extent: NumericExtent): NumericExtent {
  if (extent.min !== extent.max) return extent;

  return {
    min: extent.min - 1,
    max: extent.max + 1,
  };
}
